import { QueryClient, QueryFunction } from '@tanstack/react-query';
import { reportErrorToTech, shouldAutoReportApiError } from './error-reporter';
import { fetchWithAuth } from './api-client';

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    // 413 from some hosts/proxies returns raw HTML — detect it and throw a clean message
    if (res.status === 413) {
      throw new Error(
        '413: File too large. Please use a smaller file or contact support if you believe this is an error.',
      );
    }

    const text = (await res.text()) || res.statusText;

    // Auto-report server errors (5xx) to tech team
    if (shouldAutoReportApiError(res.status)) {
      reportErrorToTech({
        errorMessage: text,
        errorCode: res.status.toString(),
        source: 'api_error',
        url: res.url,
        additionalContext: {
          statusText: res.statusText,
          headers: Object.fromEntries(res.headers.entries()),
        },
      }).catch(() => {
        // Fail silently - don't break error handling if reporting fails

        console.error('[queryClient] Failed to auto-report API error');
      });
    }

    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options?: { timeout?: number; headers?: Record<string, string> },
): Promise<Response> {
  const headers: Record<string, string> = {};

  // Check if data is FormData - if so, don't set Content-Type (browser will set multipart/form-data)
  const isFormData = data instanceof FormData;

  if (data && !isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  // Merge custom headers from options
  if (options?.headers) {
    Object.assign(headers, options.headers);
  }

  // Set up AbortController for timeout
  const controller = new AbortController();
  const timeoutMs = options?.timeout || 30000; // Default 30 seconds
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    // Use fetchWithAuth for automatic token refresh on 401
    const res = await fetchWithAuth(url, {
      method,
      headers,
      body: data ? (isFormData ? data : JSON.stringify(data)) : undefined,
      credentials: 'include',
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    await throwIfResNotOk(res);
    return res;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeoutMs / 1000} seconds`);
    }
    throw error;
  }
}

type UnauthorizedBehavior = 'returnNull' | 'throw';
export const getQueryFn: <T>(_options: { on401: UnauthorizedBehavior }) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey.join('/') as string;

    // Use fetchWithAuth for automatic token refresh on 401
    // This ensures queries get the same token-refresh behavior as mutations
    const res = await fetchWithAuth(url, {
      credentials: 'include',
    });

    // After token refresh attempt, if still 401, handle according to behavior
    if (unauthorizedBehavior === 'returnNull' && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: 'returnNull' }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
      gcTime: Infinity,
      retry: false,
      enabled: true,
      retryDelay: () => 0,
    },
    mutations: {
      retry: false,
      onError: error => {
        console.error('🚨 Global mutation error handler:', error);

        console.error('🚨 Error type:', typeof error);

        console.error('🚨 Error details:', JSON.stringify(error, null, 2));
      },
    },
  },
});

// Add network status handling to prevent reconnection loops
queryClient.setMutationDefaults(['*'], {
  networkMode: 'offlineFirst',
});

queryClient.setQueryDefaults(['*'], {
  networkMode: 'offlineFirst',
});
